import React, { useState } from 'react';
import moment from 'moment';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'src/i18n';

export type DatePreset = '7D' | '30D' | '3M';

export function getGranularityForDateRange (start: moment.Moment, end: moment.Moment): string {
  const days = end.diff(start, 'days');
  if (days <= 2) return 'Hourly';
  if (days <= 31) return 'Daily';
  if (days <= 365) return 'Monthly';
  return 'Yearly';
}

export interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

interface HistoricalDateRangePanelProps {
  defaultPreset?: DatePreset;
  onDateChange?: (range: DateRange) => void;
  stats?: (range: DateRange) => React.ReactNode;
  graph?: (range: DateRange) => React.ReactNode;
  disablePaper?: boolean;
}

export default function HistoricalDateRangePanel ({
  defaultPreset = '7D',
  onDateChange,
  stats,
  graph,
  disablePaper = false,
}: HistoricalDateRangePanelProps) {
  const { t } = useTranslation();

  const getStartForPreset = (preset: DatePreset) => {
    if (preset === '7D') return moment().subtract(7, 'days');
    if (preset === '30D') return moment().subtract(30, 'days');
    return moment().subtract(3, 'months');
  };

  const [startDate, setStartDate] = useState<moment.Moment>(() => getStartForPreset(defaultPreset));
  const [endDate, setEndDate] = useState<moment.Moment>(moment());
  const [datePreset, setDatePreset] = useState<DatePreset | null>(defaultPreset);

  const handleDatePreset = (preset: DatePreset) => {
    const start = getStartForPreset(preset);
    const end = moment();
    setDatePreset(preset);
    setStartDate(start);
    setEndDate(end);
    onDateChange?.({ startDate: start, endDate: end });
  };

  const handleStartDateChange = (date: moment.Moment | null) => {
    if (date) {
      setDatePreset(null);
      setStartDate(date);
      onDateChange?.({ startDate: date, endDate });
    }
  };

  const handleEndDateChange = (date: moment.Moment | null) => {
    if (date) {
      setDatePreset(null);
      setEndDate(date);
      onDateChange?.({ startDate, endDate: date });
    }
  };

  const range: DateRange = { startDate, endDate };

  const content = (
    <>
      <div className='historical-date-range'>
        <div className='historical-date-range__presets'>
          {(['7D', '30D', '3M'] as const).map(preset => (
            <Chip
              key={preset}
              label={t(`device.preset${preset}`)}
              onClick={() => handleDatePreset(preset)}
              variant={datePreset === preset ? 'filled' : 'outlined'}
              color={datePreset === preset ? 'primary' : 'default'}
              className='historical-date-range__preset-chip'
            />
          ))}
        </div>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label={t('common.startDate')}
            disableFuture
            value={startDate}
            onChange={handleStartDateChange}
            maxDate={endDate}
            sx={{ width: 160 }}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label={t('common.endDate')}
            disableFuture
            value={endDate}
            onChange={handleEndDateChange}
            minDate={startDate}
            sx={{ width: 160 }}
            slotProps={{ textField: { size: 'small' } }}
          />
        </LocalizationProvider>
      </div>
      {stats?.(range)}
      {graph?.(range)}
    </>
  );

  if (disablePaper) return <>{content}</>;

  return (
    <Paper className='historical-data-panel'>
      {content}
    </Paper>
  );
}
